import { useObserver } from 'mobx-react-lite';
import { useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { GetPosFeesConfigDocument } from 'src/gql/graphql';
import type { GetPosFeesConfigQuery } from 'src/gql/graphql';
import useCart from 'src/hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { getPriceCartCustomerTypeId } from 'shared/order/utilities';

export type PosFeeItem = GetPosFeesConfigQuery['getPosFeesConfig'][number];

type UsePosFeesConfigReturn = {
  deliveryFees: PosFeeItem[];
  conditionalDeliveryFee: boolean;
  loading: boolean;
};

export const usePosFeesConfig = (): UsePosFeesConfigReturn => {
  const Cart = useCart();
  // The LD flag must gate the QUERY, not just the rendered label: this hook runs on every
  // menu/checkout page view, so an ungated query would send the full marketplace's traffic
  // to Armageddon (and through it, the POS) while the feature is dark.
  const flags = useFlags();
  const posFeesEnabled = Boolean(flags['core.cats-ecomm.pos-fee-config-display.rollout']);
  // menuType selects which leaflogix integration (med/rec) Armageddon authenticates with.
  const selectedMenuType = useObserver(() => Cart?.menuType);
  const menuType = selectedMenuType ?? 'rec';
  // Match price-cart's default recreational identity when no menu is selected.
  // A genuinely unrecognized menu remains unknown rather than inventing eligibility.
  const customerTypeId =
    menuType === 'med' || menuType === 'rec' ? getPriceCartCustomerTypeId(menuType === 'med') : null;
  const { dispensary } = useDispensary();
  // Prefer the Cart's dispensary id (always populated in cart/checkout context) and fall
  // back to the router-loaded dispensary.
  const dispensaryId = useObserver(() => Cart?.dispensary?.id ?? Cart?.order?.dispensary?.id ?? dispensary?.id ?? '');

  // Beyond the flag, gate only on having a dispensary id. Armageddon resolves the leaflogix
  // integration server-side and returns an empty list for non-POS dispensaries, so there is
  // no need to pre-check integration status on the client (doing so was silently skipping the
  // call). Use cache-and-network (not cache-first): a cache-first policy serves a stale empty
  // list if the fees were ever fetched before being configured, and never refetches. Armageddon
  // caches the POS response, so the background refetch doesn't hit the POS each time.
  const { data, loading } = useQuery(GetPosFeesConfigDocument, {
    skip: !posFeesEnabled || !dispensaryId,
    variables: { dispensaryId, menuType },
    fetchPolicy: 'cache-and-network',
  });

  const allFees = data?.getPosFeesConfig ?? [];
  // The endpoint returns every active fee; keep only automatic delivery fees — manual fees
  // are budtender-applied and payment fees depend on tender, so neither belongs in the
  // pre-cart delivery fee display. Payment fees are excluded explicitly (not just by type
  // mismatch) since a tender-dependent fee can also be flagged deliveryOnly.
  const automaticDeliveryFees = allFees.filter(
    (fee) => (fee.deliveryOnly || fee.feeType === 'delivery') && fee.feeType !== 'payment' && fee.isAutomatic
  );
  const eligible = (fee: PosFeeItem): boolean =>
    fee.appliesToAllCustomerTypes === true ||
    (fee.appliesToAllCustomerTypes === false &&
      customerTypeId != null &&
      fee.customerTypeIds?.includes(customerTypeId) === true);
  const deliveryFees = automaticDeliveryFees.filter(eligible);
  // Known nonmatches (including a restricted empty list) cannot be charged by price-cart.
  // Only genuinely unknown eligibility should hide totals or disable cheaper scheduling.
  // An empty eligible list still returns no POS label; callers retain other fee sources.
  const conditionalDeliveryFee = automaticDeliveryFees.some(
    (fee) =>
      fee.appliesToAllCustomerTypes == null ||
      (!fee.appliesToAllCustomerTypes && (customerTypeId == null || fee.customerTypeIds == null))
  );

  return { deliveryFees, conditionalDeliveryFee, loading };
};
